import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertTriangle, CheckCircle2, XCircle,
    ArrowRight, TrendingUp, TrendingDown, Minus,
    FileText, BarChart2, ClipboardCheck, Settings
} from 'lucide-react';
import {
    useBatches, useEvalResults, usePolicies,
    useStandardQA, useReviews, useHistory,
} from '../../../hooks/console/useConsoleAPI';
import { getVBg, getVText, getScoreColor, CONSOLE_VERDICT_STYLE } from '../../../constants/consoleConstants';

export default function ConsoleDashboardTab() {
    const navigate = useNavigate();

    const { data: qaData }         = useStandardQA({ size: 1 });
    const { data: batchData }      = useBatches({ size: 6 });
    const { data: policyData }     = usePolicies({ is_active: 'Y', size: 1 });
    const { data: allReviewsData } = useReviews({ size: 1000, page: 1 });

    const totalQA  = qaData?.total ?? 0;
    const totalPol = policyData?.total ?? 0;

    const { data: histData } = useHistory({ page: 1, size: 1 });
    const histCount = histData?.total ?? 0;
    const qaLoaded  = qaData !== undefined;
    const bLoaded   = batchData !== undefined;

    const batches     = batchData?.items ?? [];
    const latestBatch = batches[0];
    const prevBatch   = batches[1];
    const latestBatchId = latestBatch?.id;

    // 최신 배치 기준 FAIL / WARN 조회
    const { data: failData } = useEvalResults({
        grade: 'FAIL', size: 20,
        ...(latestBatchId ? { batch_id: latestBatchId } : {}),
    });
    const { data: warnData } = useEvalResults({
        grade: 'WARN', size: 20,
        ...(latestBatchId ? { batch_id: latestBatchId } : {}),
    });

    const failItems  = failData?.items ?? [];
    const warnItems  = warnData?.items ?? [];

    const allReviews = allReviewsData?.items ?? [];

    // 처리 완료(CLOSED)된 evaluation_result_id — 대시보드에서 완전히 제외
    const closedIds = new Set(
        allReviews
            .filter((r) => r.review_status === 'CLOSED')
            .map((r) => r.evaluation_result_id)
    );

    // 최신 배치 WARN/FAIL 중 CLOSED가 아닌 것만 표시
    const actionItems = [...failItems, ...warnItems]
        .filter((ev) => !closedIds.has(ev.id))
        .slice(0, 5);

    const allReviewedIds = new Set(allReviews.map((r) => r.evaluation_result_id));

    // 최신 배치 기준으로만 카운트
    const latestBatchItems = [...failItems, ...warnItems];
    // 리뷰 자체가 없는 것
    const unreviewedCount = latestBatchItems
        .filter((ev) => !allReviewedIds.has(ev.id)).length;
    // OPEN 리뷰 있는 것 (최신 배치 기준)
    const openIds = new Set(
        allReviews
            .filter((r) => r.review_status === 'OPEN')
            .map((r) => r.evaluation_result_id)
    );
    const openReviewCount = latestBatchItems.filter((ev) => openIds.has(ev.id)).length;

    // 전체 처리 필요 = 리뷰 미등록 + OPEN 리뷰 (모두 최신 배치 기준)
    const totalPending = unreviewedCount + openReviewCount;

    const focusStep = !qaLoaded           ? null
        : totalQA === 0                   ? 1
        : !latestBatch && bLoaded         ? 2
        : totalPending > 0                ? 3
        : null;

    const BADGE_CLS = {
        green: 'bg-emerald-100 text-emerald-700',
        red:   'bg-red-100 text-red-600',
        amber: 'bg-amber-100 text-amber-700',
        blue:  'bg-blue-100 text-blue-700',
        gray:  'bg-gray-100 text-gray-500',
    };

    const STEPS = [
        { num: 1, label: '표준 QA 등록',  path: '/ai/admin/console/standard-qa',
          badge: totalQA > 0
            ? { text: `${totalQA}건 등록됨`, color: 'green' }
            : { text: '미등록 — 여기서 시작', color: 'red' } },
        { num: 2, label: 'AI 평가 실행', path: '/ai/admin/console/evaluation',
          badge: latestBatch
            ? { text: latestBatch.batch_name, color: 'green' }
            : { text: '미실행', color: 'gray' } },
        { num: 3, label: '결과 검토',     path: '/ai/admin/console/review',
          badge: totalPending > 0
            ? { text: `처리 필요 ${totalPending}건`, color: 'amber' }
            : latestBatch ? { text: '처리 완료', color: 'green' } : { text: '없음', color: 'gray' } },
        { num: 4, label: '정책 설정',     path: '/ai/admin/console/policy',
          badge: totalPol > 0
            ? { text: `활성 ${totalPol}개`, color: 'green' }
            : { text: '없음', color: 'gray' } },
        { num: 5, label: '이력 확인',     path: '/ai/admin/console/history',
          badge: histCount > 0
            ? { text: `이력 ${histCount}건`, color: 'blue' }
            : { text: '없음', color: 'gray' } },
    ];

    // 최신 배치 지표
    const passCount  = latestBatch?.pass_count ?? 0;
    const warnCount  = latestBatch?.warn_count ?? 0;
    const failCount  = latestBatch?.fail_count ?? 0;
    const avgScore   = latestBatch?.avg_score  ?? 0;
    const batchTotal = Math.max(latestBatch?.total_count ?? 1, 1);
    const passRate   = Math.round((passCount / batchTotal) * 100);

    // 이전 배치 대비 증감
    const prevPassRate = prevBatch
        ? Math.round(((prevBatch.pass_count ?? 0) / Math.max(prevBatch.total_count ?? 1, 1)) * 100)
        : null;
    const passDiff = prevPassRate !== null ? passRate - prevPassRate : null;

    return (
        <div className="space-y-4 w-full">

            {/* ── 첫 방문 온보딩 ── */}
            {qaLoaded && bLoaded && totalQA === 0 && (
                <div className="rounded-xl border-2 border-dashed border-blue-200 bg-blue-50 px-6 py-5">
                    <p className="text-base font-bold text-blue-700 mb-1">처음 오셨군요! 👋 먼저 질문/답안을 등록해 보세요.</p>
                    <p className="text-sm text-blue-600 mb-3">
                        AI 품질 관리는 아래 4단계로 동작합니다.<br />
                        <strong>① 질문 등록 → ② AI 평가 실행 → ③ 결과 검토 → ④ 규칙 보정</strong>
                    </p>
                    <button
                        onClick={() => navigate('/ai/admin/console/standard-qa')}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                    >
                        질문/답안 등록하러 가기 <ArrowRight size={13} />
                    </button>
                </div>
            )}

            {/* ── ① 상태 알림 배너 ── */}
            {totalPending > 0 ? (
                <div
                    onClick={() => navigate('/ai/admin/console/review')}
                    className="flex items-center gap-3 px-5 py-3.5 rounded-xl bg-red-50 border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                >
                    <AlertTriangle size={16} className="text-red-500 shrink-0" />
                    <div className="flex-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        {unreviewedCount > 0 && (
                            <span className="text-base font-semibold text-red-700">
                                아직 확인하지 않은 항목 {unreviewedCount}건
                            </span>
                        )}
                        {openReviewCount > 0 && (
                            <span className="text-base font-semibold text-red-700">
                                의견 등록 후 미완료 {openReviewCount}건
                            </span>
                        )}
                        <span className="text-base text-red-500">최근 평가 기준 — 검토가 필요합니다</span>
                    </div>
                    <div className="flex items-center gap-1 text-base font-medium text-red-600 shrink-0">
                        검토하러 가기 <ArrowRight size={13} />
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-emerald-50 border border-emerald-100">
                    <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
                    <span className="text-base text-emerald-700">현재 처리 대기 항목이 없습니다</span>
                </div>
            )}

            {/* ── ② 진행 단계 트래커 ── */}
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-sb-header">품질 관리 진행 단계</p>
                    {focusStep && (
                        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                            ▶ 지금 해야 할 단계: <strong>{STEPS[focusStep - 1].label}</strong>
                        </span>
                    )}
                </div>
                <div className="flex items-stretch gap-1">
                    {STEPS.map((step, i) => {
                        const isFocus = step.num === focusStep;
                        return (
                            <React.Fragment key={step.num}>
                                {i > 0 && (
                                    <div className="flex items-center px-0.5">
                                        <ArrowRight size={11} className="text-gray-300 shrink-0" />
                                    </div>
                                )}
                                <button
                                    onClick={() => navigate(step.path)}
                                    className={`flex-1 flex flex-col rounded-lg border px-3 py-2.5 text-left transition-all hover:shadow-sm ${
                                        isFocus
                                            ? 'border-amber-400 bg-amber-50 hover:border-amber-500'
                                            : 'border-gray-100 bg-gray-50 hover:border-gray-300 hover:bg-white'
                                    }`}
                                >
                                    <span className="flex items-center gap-1.5 mb-1.5">
                                        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-white text-xs font-bold ${isFocus ? 'bg-amber-500' : 'bg-gray-400'}`}>
                                            {step.num}
                                        </span>
                                        <span className="text-xs font-semibold text-gray-600 truncate">{step.label}</span>
                                    </span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full inline-block font-medium ${BADGE_CLS[step.badge.color]}`}>
                                        {step.badge.text}
                                    </span>
                                    {isFocus && <span className="mt-1.5 text-xs text-amber-600 font-medium">← 지금 여기</span>}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* ── ③ 핵심 지표 3개 ── */}
            <div className="grid grid-cols-3 gap-3">
                {/* PASS율 */}
                <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <p className="text-sm text-gray-400 mb-1">AI 정답률</p>
                    <div className="flex items-end gap-2">
                        <span className={`text-3xl font-bold ${getScoreColor(passRate)}`}>
                            {passRate}<span className="text-base font-normal text-gray-400">%</span>
                        </span>
                        {passDiff !== null && (
                            <span className={`text-base font-semibold mb-1 flex items-center gap-0.5 ${passDiff > 0 ? 'text-emerald-500' : passDiff < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                                {passDiff > 0 ? <TrendingUp size={12} /> : passDiff < 0 ? <TrendingDown size={12} /> : <Minus size={12} />}
                                {passDiff > 0 ? `+${passDiff}` : passDiff}%p
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1 truncate">{latestBatch?.batch_name ?? '평가 기록 없음'}</p>
                </div>

                {/* 평균 점수 */}
                <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <p className="text-sm text-gray-400 mb-1">평균 점수</p>
                    <div className="flex items-end gap-2">
                        <span className={`text-3xl font-bold ${getScoreColor(avgScore)}`}>
                            {avgScore}<span className="text-base font-normal text-gray-400">점</span>
                        </span>
                        {prevBatch && (
                            <span className={`text-base font-semibold mb-1 flex items-center gap-0.5 ${avgScore - prevBatch.avg_score > 0 ? 'text-emerald-500' : avgScore - prevBatch.avg_score < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                                {avgScore - prevBatch.avg_score > 0 ? <TrendingUp size={12} /> : avgScore - prevBatch.avg_score < 0 ? <TrendingDown size={12} /> : <Minus size={12} />}
                                {avgScore - prevBatch.avg_score > 0 ? '+' : ''}{(avgScore - prevBatch.avg_score).toFixed(1)}
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2 mt-1">
                        <span className="text-sm text-emerald-500">정상 {passCount}</span>
                        <span className="text-sm text-amber-500">주의 {warnCount}</span>
                        <span className="text-sm text-red-500">개선필요 {failCount}</span>
                    </div>
                </div>

                {/* 검토 대기 — 미등록 + OPEN 분리 표시 */}
                <div
                    onClick={() => navigate('/ai/admin/console/review')}
                    className={`rounded-xl border p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${totalPending > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'}`}
                >
                    <p className="text-sm text-gray-400 mb-1">검토 대기</p>
                    <span className={`text-3xl font-bold ${totalPending > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                        {totalPending}<span className="text-base font-normal text-gray-400">건</span>
                    </span>
                    {totalPending > 0 ? (
                        <div className="flex flex-col gap-0.5 mt-1">
                            {unreviewedCount > 0 && (
                                <p className="text-base text-red-400">미등록 {unreviewedCount}건</p>
                            )}
                            {openReviewCount > 0 && (
                                <p className="text-base text-amber-500">완료 대기 {openReviewCount}건</p>
                            )}
                        </div>
                    ) : (
                        <p className="text-base mt-1 text-gray-400">모두 처리됨</p>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* ── ④ 즉시 처리할 항목 (최신 배치 기준) ── */}
                <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <p className="text-base font-semibold text-sb-header">즉시 처리할 항목</p>
                            {latestBatch && (
                                <p className="text-sm text-gray-400 mt-0.5">{latestBatch.batch_name} 기준</p>
                            )}
                        </div>
                        <button
                            onClick={() => navigate('/ai/admin/console/review')}
                            className="text-base text-sb-primary hover:underline flex items-center gap-0.5"
                        >
                            전체 보기 <ArrowRight size={10} />
                        </button>
                    </div>
                    <div className="space-y-1.5">
                        {actionItems.map((ev) => {
                            const VIcon     = CONSOLE_VERDICT_STYLE[ev.grade]?.icon;
                            const hasReview = allReviewedIds.has(ev.id); // OPEN 리뷰 있음
                            return (
                                <div
                                    key={ev.id}
                                    onClick={() => navigate('/ai/admin/console/review')}
                                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                                >
                                    {VIcon && <VIcon size={14} className={getVText(ev.grade)} />}
                                    <span className="flex-1 text-base text-gray-700 truncate">{ev.question}</span>
                                    <span className={`text-base font-bold shrink-0 ${getScoreColor(ev.final_score)}`}>{ev.final_score}점</span>
                                    <span className={`text-sm px-1.5 py-0.5 rounded-full font-medium shrink-0 ${getVBg(ev.grade)} ${getVText(ev.grade)}`}>
                                        {{ FAIL: '개선 필요', WARN: '주의', PASS: '정상' }[ev.grade] ?? ev.grade}
                                    </span>
                                    {hasReview && (
                                        <span className="text-sm px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 shrink-0">검토중</span>
                                    )}
                                </div>
                            );
                        })}
                        {actionItems.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-8 gap-2 text-gray-300">
                                <CheckCircle2 size={28} />
                                <p className="text-base text-gray-400">
                                    {latestBatch ? '최신 배치에 처리할 항목이 없습니다' : '평가를 실행하세요'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── ⑤ 배치별 PASS율 추이 ── */}
                <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <p className="text-base font-semibold text-sb-header mb-4">평가 회차별 정답률 추이</p>
                    {batches.length > 0 ? (
                        <>
                            <div className="flex items-end gap-2" style={{ height: '120px' }}>
                                {[...batches].reverse().map((b) => {
                                    const t        = Math.max(b.total_count ?? 1, 1);
                                    const rate     = Math.round(((b.pass_count ?? 0) / t) * 100);
                                    const isLatest = b.id === latestBatch?.id;
                                    const barH     = Math.max((rate / 100) * 80, 4);
                                    return (
                                        <div
                                            key={b.id}
                                            className="flex-1 flex flex-col items-center cursor-pointer"
                                            style={{ gap: '4px' }}
                                            onClick={() => navigate('/ai/admin/console/evaluation')}
                                            title={`${b.batch_name} — PASS ${rate}%`}
                                        >
                                            <span className="text-sm font-semibold text-gray-500">{rate}%</span>
                                            <div
                                                className="w-full rounded-t transition-all"
                                                style={{
                                                    height: `${barH}px`,
                                                    backgroundColor: isLatest ? '#2563eb' : rate >= 80 ? '#10b981' : rate >= 60 ? '#f59e0b' : '#ef4444',
                                                    opacity: isLatest ? 1 : 0.6,
                                                }}
                                            />
                                            <span className="text-sm text-gray-400 truncate w-full text-center leading-tight">
                                                {b.batch_name?.replace(/\d{4}-\d{2}-\d{2}\s?/, '').slice(0, 8) || b.batch_name?.slice(-8)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50">
                                {[
                                    { color: '#2563eb', label: '최신', opacity: 1 },
                                    { color: '#10b981', label: '≥80%', opacity: 0.6 },
                                    { color: '#f59e0b', label: '≥60%', opacity: 0.6 },
                                    { color: '#ef4444', label: '<60%', opacity: 0.6 },
                                ].map(({ color, label, opacity }) => (
                                    <span key={label} className="flex items-center gap-1.5 text-sm text-gray-500">
                                        <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', backgroundColor: color, opacity }} />
                                        {label}
                                    </span>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-300">
                            <BarChart2 size={28} />
                            <p className="text-base text-gray-400">평가를 실행하면 추이가 표시됩니다</p>
                            <button
                                onClick={() => navigate('/ai/admin/console/evaluation')}
                                className="mt-1 text-sm text-sb-primary hover:underline"
                            >
                                평가 실행하러 가기
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
